import { Fragment, useId, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { Card } from '../../ui'
import { getAiInventory, referenceOf, type AiInventory, type AiInventoryEntryFinding } from '../../lib/api'
import { AiGate } from './AiArea'
import { severityTag } from './LiveDashboard'
import { describeFailure, useApi } from './useApi'
import './live.css'

/**
 * AI assets (owner, 2026-10-12): every AI asset Defenssive can see, on one page of the left menu.
 *
 * The second version (owner: "dashboard is bad ... it does not align to us"). One table, in the shape of the findings
 * lists: an asset per row with its kind, one line of what it is, the worst open finding about it, and where to manage
 * it. The ones with findings come first, as Microsoft Defender's AI inventory and Wiz's AI-SPM put risk before the
 * list. A row opens to its details: settings, model deployments, permissions, and each finding on its own line, with
 * the deployment it is about. Read live when the page opens; nothing here is a finding of its own.
 */
export function AiAssets() {
  return (
    <AiGate>
      <AiAssetsBody />
    </AiGate>
  )
}

type Kind = 'azure' | 'app' | 'agent'

/** One row: any asset, in the one shape the table draws. */
interface Asset {
  key: string
  kind: Kind
  name: string
  badge: string
  summary: string
  findings: AiInventoryEntryFinding[]
  manage: { label: string; href: string } | null
  detail: ReactNode
  reachesData: boolean
  /** Its permissions could not be read: it says "not read", never "none" (re-review RR06). */
  permissionsUnread?: boolean
}

type Filter = 'all' | 'attention' | Kind

const RANK = ['Critical', 'High', 'Medium', 'Low']
const rank = (s: string) => {
  const i = RANK.indexOf(s)
  return i < 0 ? RANK.length : i
}
const worst = (findings: AiInventoryEntryFinding[]) =>
  findings.length === 0 ? null : findings.map((f) => f.severity).sort((a, b) => rank(a) - rank(b))[0]!

/** Where an Entra application or agent is managed: its enterprise application page. */
export function entraLink(objectId: string, appId: string | null | undefined): string | null {
  if (!appId) return null
  return `https://entra.microsoft.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Overview/objectId/${encodeURIComponent(objectId)}/appId/${encodeURIComponent(appId)}`
}

/** Where an Azure resource is managed: its page in the Azure portal. */
export function azureLink(resourceId: string): string {
  return `https://portal.azure.com/#resource${resourceId}/overview`
}

function AiAssetsBody() {
  const inventory = useApi(() => getAiInventory(), [])
  const [filter, setFilter] = useState<Filter>('all')

  if (inventory.state === 'loading') return <div className="live-empty">Reading your AI assets…</div>
  if (inventory.state === 'error') return <div className="live-empty">{describeFailure(inventory.status)}</div>
  const inv = inventory.data
  if (!inv.onMonitor) return null

  const assets = assetsOf(inv).sort((a, b) => {
    const wa = worst(a.findings)
    const wb = worst(b.findings)
    if (wa !== wb) return (wa ? rank(wa) : 9) - (wb ? rank(wb) : 9)
    return a.name.localeCompare(b.name)
  })

  const count = (k: Kind) => assets.filter((a) => a.kind === k).length
  const attention = assets.filter((a) => a.findings.length > 0)
  const openFindings = new Set(assets.flatMap((a) => a.findings.map((f) => f.id))).size
  const reaching = assets.filter((a) => a.reachesData).length
  const unread = assets.filter((a) => a.permissionsUnread).length
  const shown = filter === 'all' ? assets : filter === 'attention' ? attention : assets.filter((a) => a.kind === filter)
  const deployments = inv.azure ? inv.azure.accounts.reduce((n, a) => n + (a.deployments?.length ?? 0), 0) : null

  const filters: [Filter, string, number][] = [
    ['all', 'All', assets.length],
    ['attention', 'Need attention', attention.length],
    ['azure', 'Azure AI', count('azure')],
    ['app', 'Applications', count('app')],
    ['agent', 'Agents', count('agent')],
  ]

  return (
    <>
      <div className="fi-strip fi-strip-4" role="group" aria-label="AI assets at a glance">
        <Tile label="AI assets" value={assets.length}
          sub={`${count('azure')} Azure · ${count('app')} applications · ${count('agent')} agents`} />
        <Tile label="Need attention" value={attention.length} warn={attention.length > 0}
          sub={`With ${openFindings} open finding${openFindings === 1 ? '' : 's'}`} />
        <Tile label="Can reach company data" value={reaching}
          sub={unread > 0 ? `At least: ${unread} could not be read` : 'Organisation-wide or application permissions'} />
        <Tile label="Azure model deployments" value={deployments}
          sub={inv.azure ? 'Across your Azure AI resources' : 'Not read in this build'} />
      </div>

      <Card style={{ marginTop: 'var(--space-4)' }}>
        <div className="ai-assets-filters" role="group" aria-label="Show">
          {filters.map(([value, label, n]) => (
            <button key={value} type="button" className="findings-filter" aria-pressed={filter === value}
              onClick={() => setFilter(value)} disabled={n === 0 && value !== 'all'}>
              {label} <span className="ai-assets-count">{n}</span>
            </button>
          ))}
        </div>

        {inv.azure ? <AzureNote azure={inv.azure} /> : null}
        {!inv.complete ? <p className="settings-note">Part of your applications and agents could not be read just now, so this may be incomplete.</p> : null}
        {inv.truncated ? <p className="settings-note">Only the first 100 AI applications and the first 100 agents of each kind are listed; your tenant has more.</p> : null}

        {shown.length === 0 ? (
          <p className="settings-note">No AI assets found.</p>
        ) : (
          <div className="ai-assets-table-wrap">
            <table className="table ai-assets-table">
              <thead>
                <tr>
                  <th scope="col">Asset</th>
                  <th scope="col">Open findings</th>
                  <th scope="col"><span className="sr-only">Manage</span></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((a) => <AssetRow key={a.key} asset={a} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="settings-hint" style={{ marginTop: 'var(--space-3)' }}>
        Read from Microsoft when this page opens: configuration only, never keys, prompts or answers. AI applications are
        those from publishers other than Microsoft; agents are those with their own identity in Microsoft Entra, such as
        Copilot Studio agents.
      </p>
    </>
  )
}

function Tile({ label, value, sub, warn = false }: { label: string; value: number | null; sub: string; warn?: boolean }) {
  return (
    <div className={warn ? 'fi-tile fi-warn' : 'fi-tile'}>
      <span className="fi-label">{label}</span>
      <span className={warn ? 'fi-value fi-bad' : 'fi-value'}>{value ?? '—'}</span>
      <span className="fi-sub">{sub}</span>
    </div>
  )
}

function AzureNote({ azure }: { azure: NonNullable<AiInventory['azure']> }) {
  const note =
    azure.outcome === 'completed'
      ? azure.partial ? 'Part of Azure could not be listed just now, so the Azure AI resources may be incomplete.' : null
      : azure.outcome === 'no_subscriptions'
        ? 'No Azure subscription has given Defenssive the Reader role, so no Azure AI resources are listed. Settings, under Azure, says how to add one.'
        : 'Azure could not be read just now, so your Azure AI resources are not listed. Try again later.'
  return note ? <p className="settings-note">{note}</p> : null
}

function AssetRow({ asset: a }: { asset: Asset }) {
  const [open, setOpen] = useState(false)
  const detailId = useId()
  const w = worst(a.findings)
  return (
    <Fragment>
      <tr className={open ? 'ai-asset-row is-open' : 'ai-asset-row'}>
        <td>
          <button type="button" className="ai-asset-toggle" aria-expanded={open} aria-controls={detailId} onClick={() => setOpen((o) => !o)}>
            <svg className="ai-asset-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
            <span className="ai-asset-name">{a.name}</span>
          </button>
          <span className="ai-asset-badge">{a.badge}</span>
          <div className="ai-asset-summary">{a.summary}</div>
        </td>
        <td className="ai-asset-risk">
          {w ? <>{severityTag(w)}<span className="ai-asset-risk-count">{a.findings.length}</span></> : <span className="ai-asset-none">None open</span>}
        </td>
        <td className="ai-asset-manage">
          {a.manage ? (
            <a href={a.manage.href} target="_blank" rel="noopener noreferrer">
              {a.manage.label}<span className="sr-only"> (opens in a new tab)</span>
            </a>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr className="ai-asset-detail-row">
          <td colSpan={3} id={detailId}>
            <div className="ai-asset-detail">
              {a.detail}
              <FindingList findings={a.findings} />
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  )
}

/** Each finding on its own line, severity first, with the deployment it is about when it is about one. */
function FindingList({ findings }: { findings: AiInventoryEntryFinding[] }) {
  const navigate = useNavigate()
  if (findings.length === 0) return <p className="ai-assets-clear">No open findings about it.</p>
  const sorted = [...findings].sort((a, b) => rank(a.severity) - rank(b.severity))
  return (
    <div>
      <h4 className="ai-asset-detail-head">Open findings</h4>
      <ul className="ai-asset-findings">
        {sorted.map((f) => (
          <li key={f.id}>
            {severityTag(f.severity)}
            <a
              href={`/findings/${referenceOf(f.id)}`}
              onClick={(e) => {
                e.preventDefault()
                navigate(`/findings/${referenceOf(f.id)}`, { state: { from: '/ai-assets' } })
              }}
            >
              {f.title}
            </a>
            {f.subject ? <span className="ai-asset-subject">{f.subject}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

const EXPOSURE: Record<string, string> = {
  open: 'Open to the internet',
  selected_networks: 'Selected networks only',
  disabled: 'Disabled',
}

function Fact({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return (
    <span className={bad ? 'ai-assets-fact is-bad' : 'ai-assets-fact'}>
      <span className="ai-assets-fact-label">{label}</span> {value}
    </span>
  )
}

/** How many permissions show before the rest fold away behind "Show all". */
const SHOWN = 4

function Permissions({ label, items, none }: { label: string; items: string[]; none?: string }) {
  if (items.length === 0) return none ? <p className="ai-inventory-line">{none}.</p> : null
  const shown = items.slice(0, SHOWN)
  const rest = items.slice(SHOWN)
  return (
    <div className="ai-inventory-line">
      <span className="ai-inventory-label">{label} ({items.length}):</span> {shown.join(', ')}
      {rest.length > 0 ? (
        <details className="ai-inventory-more">
          <summary>Show all {items.length}<span className="sr-only"> {label.toLowerCase()}</span></summary>
          {rest.join(', ')}
        </details>
      ) : null}
    </div>
  )
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** Every asset in the inventory, as rows. */
function assetsOf(inv: AiInventory): Asset[] {
  const rows: Asset[] = []

  for (const a of inv.azure?.accounts ?? []) {
    const deployments = a.deployments ?? []
    const logging = a.loggingSettings === null ? 'Not read' : a.loggingSettings > 0 ? 'Sends logs' : 'Not logged'
    rows.push({
      key: a.id,
      kind: 'azure',
      name: a.name,
      badge: a.kind === 'AIServices' ? 'AI Foundry' : 'Azure OpenAI',
      summary: [
        [a.location, a.resourceGroup].filter(Boolean).join(' · '),
        a.deployments === null ? 'deployments not read'
          : deployments.length === 0 ? 'no model deployments'
            : `${plural(deployments.length, 'model deployment')}: ${deployments.map((d) => d.model ?? d.name).join(', ')}`,
      ].filter(Boolean).join(' · '),
      findings: a.findings,
      manage: { label: 'Open in Azure portal', href: azureLink(a.id) },
      reachesData: false,
      detail: (
        <>
          <div className="ai-assets-facts">
            <Fact label="Key sign-in" value={a.keySignIn ? 'Accepted' : 'Off'} bad={a.keySignIn} />
            <Fact label="Public network" value={EXPOSURE[a.publicNetwork] ?? a.publicNetwork} bad={a.publicNetwork === 'open'} />
            {a.privateEndpoints > 0 ? <Fact label="Private endpoints" value={String(a.privateEndpoints)} /> : null}
            <Fact label="Logging" value={logging} bad={a.loggingSettings === 0} />
          </div>
          {deployments.length > 0 ? (
            <table className="evidence-table ai-asset-deployments">
              <thead><tr><th scope="col">Deployment</th><th scope="col">Model</th><th scope="col">Content filter</th></tr></thead>
              <tbody>
                {deployments.map((d) => (
                  <tr key={d.name}>
                    <td>{d.name}</td>
                    <td>{[d.model, d.modelVersion].filter(Boolean).join(' · ') || 'Not recorded'}</td>
                    <td>{d.contentFilter ?? 'None'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      ),
    })
  }

  for (const h of inv.azure?.hubs ?? []) {
    rows.push({
      key: h.id,
      kind: 'azure',
      name: h.name,
      badge: h.kind === 'Project' ? 'AI Foundry project' : 'AI Foundry hub',
      summary: [h.location, h.resourceGroup].filter(Boolean).join(' · '),
      findings: h.findings,
      manage: { label: 'Open in Azure portal', href: azureLink(h.id) },
      reachesData: false,
      detail: (
        <div className="ai-assets-facts">
          <Fact label="Public network" value={EXPOSURE[h.publicNetwork] ?? h.publicNetwork} bad={h.publicNetwork === 'open'} />
        </div>
      ),
    })
  }

  for (const a of inv.apps) {
    const orgWide = a.organisationWideScopes.length
    const total = orgWide + a.personalScopes.length + a.applicationPermissions.length
    const link = entraLink(a.id, a.appId)
    const unread = a.permissionsRead === false
    rows.push({
      key: a.id,
      kind: 'app',
      name: a.name,
      badge: 'AI application',
      permissionsUnread: unread,
      summary: unread ? 'Permissions could not be read' : total === 0 ? 'No permissions granted'
        : [
          orgWide ? `${plural(orgWide, 'permission')} for everyone` : null,
          a.personalScopes.length ? `${plural(a.personalScopes.length, 'permission')} by individual people` : null,
          a.applicationPermissions.length ? plural(a.applicationPermissions.length, 'application permission') : null,
        ].filter(Boolean).join(' · '),
      findings: a.findings,
      manage: link ? { label: 'Open in Microsoft Entra', href: link } : null,
      reachesData: orgWide > 0 || a.applicationPermissions.length > 0,
      detail: unread ? <p className="settings-note">Its consents and permissions could not be read just now.</p> : (
        <>
          <Permissions label="Consented for everyone" items={a.organisationWideScopes} none="No organisation-wide consent" />
          <Permissions label="Consented by individual people" items={a.personalScopes} />
          <Permissions label="Application permissions" items={a.applicationPermissions} />
        </>
      ),
    })
  }

  for (const a of inv.agents) {
    const link = entraLink(a.id, a.appId)
    const unread = a.permissionsRead === false
    rows.push({
      key: a.id,
      kind: 'agent',
      name: a.name,
      badge: a.kind,
      permissionsUnread: unread,
      summary: unread ? 'Permissions and roles could not be read' : [
        a.applicationPermissions.length ? plural(a.applicationPermissions.length, 'application permission') : 'No application permissions',
        a.directoryRoles.length ? plural(a.directoryRoles.length, 'directory role') : null,
      ].filter(Boolean).join(' · '),
      findings: a.findings,
      manage: link ? { label: 'Open in Microsoft Entra', href: link } : null,
      reachesData: a.applicationPermissions.length > 0,
      detail: unread ? <p className="settings-note">Its permissions and roles could not be read just now.</p> : (
        <>
          <Permissions label="Application permissions" items={a.applicationPermissions} none="No application permissions" />
          <Permissions label="Directory roles" items={a.directoryRoles} />
        </>
      ),
    })
  }

  return rows
}
